import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import notify from 'devextreme/ui/notify';
import validationEngine from 'devextreme/ui/validation_engine';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { getDialogYesNoOptions, getNotificationTimeout, sendNotificationMessage } from '../../../types/resource';
import { LoggingService } from '../../../framework/logging/logging.service';
import { custom } from 'devextreme/ui/dialog';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { TaskDataService } from '../../../shared/services/task-data.service';
import { ITaskModificationData, ITaskRequest, TaskModificationData, TaskRequest } from '../../../modules/tasks/model/types';
import { MessageService } from '../../../shared/components/framework/services/message.service';
import { FormUtils } from '../../../shared/components/framework/services/form-builder.service';
import { LoanDataBase } from '../baseclass/loandata-base';

@Component({
  standalone: false,
  selector: 'app-modification',
  templateUrl: './modification.component.html',
  styleUrl: './modification.component.scss'
})
export class ModificationComponent extends LoanDataBase {
    @ViewChild('formElement') formElement!: ElementRef;
    @Input() taskTemplateName: string;
    @Input() task: ITaskRequest;
    @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();
    shouldDisableCheckbox: boolean = false;
    isGridInEditMode: boolean = false;
    modificationData: ITaskModificationData;
    files: File[] = [];
    isLoading: boolean = false;
    doNotExcludeSelectedLoans: boolean = false;
    requestTypes: any[] = [];
    today: Date = new Date();
    e: any;
    disableChangeCheckboxes: boolean = false;
    selectedDealLoanNumbers: string[] = [];
    isEditable: boolean = false;
    mandatoryFiles: boolean = false;
    // Form configuration management
    formItemsBehaviourSubject$ = new BehaviorSubject<any[]>([]);
    formItems$ = this.formItemsBehaviourSubject$.asObservable();
    formItems: any[] = [];
    currentFormInstance: any = null;

    // Column count management
    formColCountBehaviourSubject$ = new BehaviorSubject<number>(2);
    formColCount$ = this.formColCountBehaviourSubject$.asObservable();
    formColCount: number = 2;

    // Current request type and state tracking
    currentRequestType: string = "";
    override showInactiveLoans: boolean = false;

    constructor(taskDataService: TaskDataService, private readonly loggingService: LoggingService,
       private readonly messageService: MessageService) {
        super(taskDataService);

      // Bind methods
      this.onRequestTypeSelectionChanged = this.onRequestTypeSelectionChanged.bind(this);
      this.onLoanNumberChanged = this.onLoanNumberChanged.bind(this);
      this.onShowInactiveLoansChanged = this.onShowInactiveLoansChanged.bind(this);

      // Subscribe to form items
      this.formItems$.subscribe((items: any[]) => {
        this.formItems = items;
      });

      // Subscribe to column count
      this.formColCount$.subscribe((colcount: number) => {
        this.formColCount = colcount;
      });

      // Subscribe to loan data changes
      this.loanNumbers$.subscribe((data) => {
        this.loanNumbers = data;
        if (this.currentRequestType !== 'MODIFICATION') {
          this.updateLoanDataInFormConfig();
        }
      });
    }

      private updateCheckboxDisabledState(): void {
    const hasItems = this.modificationData.loanNumbers && this.modificationData.loanNumbers.length > 0;
    this.shouldDisableCheckbox = this.isGridInEditMode || hasItems;

    // Update checkbox in form configuration for non-LOAN ADVANCE forms
    if (
      this.formItems &&
      this.formItems.length > 0 &&
      this.currentRequestType !== "LOAN ADVANCE" && this.currentRequestType.toUpperCase() !== "AUDIT CONFIRM"
    ) {
      const updated = this.updateCheckboxInFormItems(
        this.formItems,
        this.shouldDisableCheckbox,
      );
      if (updated) {
        this.formItemsBehaviourSubject$.next([...this.formItems]);
      }
    }

  }

    private updateCheckboxInFormItems(items: any[], disabled: boolean): boolean {
    for (const item of items) {
      // Check if this is the show inactive loans checkbox (by CSS class and editor type)
      if (
        item.editorType === "dxCheckBox" &&
        item.cssClass === "loan-checkbox-center-align"
      ) {
        if (!item.editorOptions) {
          item.editorOptions = {};
        }

        // Preserve the current value from the component property
        const currentValue = this.showInactiveLoans;
        // Update the disabled state while preserving the value
        item.editorOptions.disabled = disabled;

        // Make sure the value is properly set in the editor options
        item.editorOptions.value = currentValue;

        return true; // Found and updated
      }

      // Check nested items recursively
      if (item.itemType === "group" && item.items) {
        if (this.updateCheckboxInFormItems(item.items, disabled)) {
          return true;
        }
      }

      if (item.items && Array.isArray(item.items)) {
        if (this.updateCheckboxInFormItems(item.items, disabled)) {
          return true;
        }
      }
    }
    return false; // Not found
  }

  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

    getLoanData() {
    // console.log('getLoanData called, loanAmounts:', this.modificationData.loanNumbers);

    if (this.modificationData.loanNumbers && this.modificationData.loanNumbers.length > 0) {
      // Get the first loan number - handle both string array and object array
      const firstLoanNumber = typeof this.modificationData.loanNumbers[0] === 'string'
        ? this.modificationData.loanNumbers[0]
        : this.modificationData.loanNumbers[0].loanNumber;

      // Search in allLoanData to find loan info regardless of active/inactive status
      const firstLoan = this.allLoanData.find((ld) => ld.name === firstLoanNumber);

      // console.log('First loan:', firstLoan);
      // console.log('First loan properties:', firstLoan ? Object.keys(firstLoan) : 'N/A');

      // If firstLoan is not found, return all loan data (fallback)
      if (!firstLoan) {
        console.warn('First loan not found in loan data, returning all loans');
        return this.loanNumbers;
      }

      // Extract deal identifier from loan name (first 6 digits of the loan number)
      // Format: "240002002 - Xtreme-DDTL" -> extract "240002"
      const extractDealId = (loanName: string): string | null => {
        if (!loanName) return null;
        // Extract just the loan number part (before the " - ")
        const loanNumberPart = loanName.split(' - ')[0];
        // Return first 6 digits
        return loanNumberPart.length >= 6 ? loanNumberPart.substring(0, 6) : null;
      };

      const firstLoanDealId = extractDealId(firstLoan.name);
      // console.log('Extracted deal ID from first loan (first 6 digits):', firstLoanDealId);

      if (!firstLoanDealId) {
        console.warn('Could not extract deal ID from loan name, returning all loans');
        return this.loanNumbers;
      }

      const existingLoanNumbers = this.modificationData.loanNumbers.map((item) =>
        typeof item === 'string' ? item : item.loanNumber
      );
      // console.log('Existing loan numbers:', existingLoanNumbers);

      // Filter loans that have the same deal ID (same first 6 digits)
      const filteredLoans = this.loanNumbers.filter((l) => {
        const loanDealId = extractDealId(l.name);
        return loanDealId === firstLoanDealId;
      });
      // console.log('Filtered loans by deal (first 6 digits):', filteredLoans.map(l => l.name));

      if (this.doNotExcludeSelectedLoans) {
        //first pass if editing
        this.doNotExcludeSelectedLoans = false;
        // console.log('Returning filtered loans (editing mode)');
        return filteredLoans;
      }

      const finalLoans = filteredLoans.filter((l) => !existingLoanNumbers.includes(l.name));
      // console.log('Final filtered loans (excluding selected):', finalLoans.map(l => l.name));
      return finalLoans;
    }

    // If no loan amounts but we have selectedDealLoanNumbers, filter by those
    if (this.selectedDealLoanNumbers && this.selectedDealLoanNumbers.length > 0) {
      // console.log('Filtering by selectedDealLoanNumbers:', this.selectedDealLoanNumbers);
      return this.loanNumbers.filter((l) => this.selectedDealLoanNumbers.includes(l.name));
    }

    // console.log('Returning all loan data');
    return this.loanNumbers;
  }

    onRequestTypeSelectionChanged(e: any) {
      // Safety check for selectedItem
      if (!e.selectedItem || !e.selectedItem.name) {
        console.warn('Invalid selection event:', e);
        return;
      }

      this.currentRequestType = e.selectedItem.name;
      const value = e.selectedItem.name;
      this.disableChangeCheckboxes = value === 'New Loan Modification';

      let config = [];
      switch (value.toUpperCase()) {
        case "MODIFICATION":
          this.mandatoryFiles = !this.isEditable;
          config = [
            {
              itemType: "simple",
              name: "loanAmountsGridWithCheckbox",
              colSpan: 2,
              label: { visible: false },
              template: "loanAmountsGridTemplate",
            },
            {
              itemType: "group",
              colCount: 3,
              colSpan: 2,
              items: [
                // {
                //     dataField: "newInvestor",
                //     editorType: "dxCheckBox",
                //     label: { text: "New Investor?" },
                //     colSpan: 1,
                //     cssClass: "dx-widget",
                //   },
                  {
                    dataField: "isUrgent",
                    editorType: "dxCheckBox",
                    label: { text: "Rush Job?" },
                    colSpan: 1,
                    cssClass: "dx-widget",
                  },
              ],
            },
            // Documents Received Date
            {
              colSpan: 2,
              dataField: "documentsReceivedDate",
              editorType: "dxDateBox",
              editorOptions: { required: true },
              label: { text: "Date Documents Received" },
              validationRules: [{
                type: "required",
                message: "Date Documents Received is required"
              }]
            },
            // Modification Date
            {
              colSpan: 2,
              dataField: "modificationDate",
              editorType: "dxDateBox",
              editorOptions: { required: true },
              label: { text: "Modification Date" },
              validationRules: [{
                type: "required",
                message: "Modification Date is required"
              }]
            },
            {
              dataField: "description",
              editorType: "dxTextArea",
              label: { text: "Description" },
              colSpan: 2,
              cssClass: "dx-widget",
              editorOptions: {
                required: "true",
                defaultValue: "",
                height: 100,
              },
              validationRules: [
                { type: "required", message: "Description is required" },
              ],
            },
            // Upload Instructions Label
            {
              colSpan: 2,
              template: function() {
                return "<p style='font-weight: 500; margin: 8px 0; color: #333;'>Upload Modification Documents to the Legal>Amendments & Syndications folder</p>";
              }
            },
            // File Upload
            {
              // work around to bind file upload component to form and validate it
              dataField: "fileUploadProxy",
              colSpan: 2,
              itemType: "simple",
              label: { visible: false },
              editorType: "dxTextBox",
              editorOptions: {
                visible: false,
                value: this.files.length.toString(),
              },
              // validationRules: [
              //   {
              //     type: "custom",
              //     message: "At least one file is required",
              //     validationCallback: (e) => this.onValidateFileAttachments(e),
              //     reevaluate: true,
              //   },
              // ],
            },
            {
              colSpan: 2,
              horizontalAlignment: "center",
              itemType: "simple",
              name: "fileUpload",
              label: { text: "Upload Files" },
              template: "fileUploadTemplate",
            },
            // Submit Button
            {
              colSpan: 2,
              cssClass: "borrow-submit",
              horizontalAlignment: "center",
              editorType: "dxButton",
              editorOptions: {
                text: 'Submit',
                useSubmitBehavior: true
              }
            }
          ];
          break;
        case "LOAN MODIFICATION - OTHER":
          config = [
            // Loan number with checkbox
            {
              itemType: "group",
              colSpan: 2,
              colCount: 4,
              items: [
                {
                  dataField: "loanNumber",
                  editorType: "dxSelectBox",
                  colSpan: 3,
                  editorOptions: {
                    items: this.loanNumbers,
                    valueExpr: 'name',
                    displayExpr: 'name',
                    required: true,
                    searchMode: 'contains',
                    searchEnabled: true,
                    onValueChanged: this.onLoanNumberChanged,
                    itemTemplate: this.loanItemTemplate
                  },
                  label: { text: "Loan Number" },
                  validationRules: [{
                    type: "required",
                    message: "Loan Number is required"
                  }]
                },
                // {
                //   editorType: "dxCheckBox",
                //   cssClass: "loan-checkbox-center-align",
                //   label: { visible: false },
                //   editorOptions: {
                //     text: this.activeLoanCheckboxText,
                //     onValueChanged: this.onShowInactiveLoansChanged,
                //     value: false
                //   }
                // }
              ]
            },
            // Investor
            // {
            //   dataField: "newInvestor",
            //   editorType: "dxCheckBox",
            //   label: { text: "New Investor?" },
            //   colSpan: 1,
            //   cssClass: "dx-widget",
            // },
            // Documents Received Date
            {
              colSpan: 2,
              dataField: "documentsReceivedDate",
              editorType: "dxDateBox",
              editorOptions: { required: true },
              label: { text: "Date Documents Received" },
              validationRules: [{
                type: "required",
                message: "Date Documents Received is required"
              }]
            },
            // Modification Date
            {
              colSpan: 2,
              dataField: "modificationDate",
              editorType: "dxDateBox",
              editorOptions: { required: true },
              label: { text: "Modification Date" },
              validationRules: [{
                type: "required",
                message: "Modification Date is required"
              }]
            },
            // Tax/Insurance Change Checkbox - ENABLED for other modifications
            {
              dataField: "taxInsuranceChange",
              editorType: "dxCheckBox",
              label: { visible: false },
              editorOptions: {
                text: 'Tax/Insurance Change',
                disabled: false
              }
            },
            // Treasury Change Checkbox - ENABLED for other modifications
            {
              dataField: "treasuryChange",
              editorType: "dxCheckBox",
              label: { visible: false },
              editorOptions: {
                text: 'Treasury Change',
                disabled: false
              }
            },
            // Upload Instructions Label
            {
              colSpan: 2,
              template: function() {
                return "<p style='font-weight: 500; margin: 8px 0; color: #333;'>Upload Modification Documents to the Legal>Amendments & Syndications folder</p>";
              }
            },
            // File Upload
            {
              colSpan: 2,
              template: 'fileUploadTemplate'
            },
            // Submit Button
            {
              colSpan: 2,
              cssClass: "borrow-submit",
              horizontalAlignment: "center",
              editorType: "dxButton",
              editorOptions: {
                text: 'Submit',
                useSubmitBehavior: true
              }
            }
          ];
          break;

        default:
          // Default configuration for unknown request types
          config = [];
          break;
      }

      this.formItemsBehaviourSubject$.next(config);
    }

    onValidateFileAttachments(e: any): boolean {
    if (this.mandatoryFiles && this.files.length == 0) {
        e.isValid = false;
        return false;
      }
      e.isValid = true;
      return true;
    }

    async onSubmit(e: any)
    {
      e.preventDefault();

      let validationResult = validationEngine.validateGroup("formGroup");

      if (validationResult.isValid)
      {
        // Validate that at least one loan number is selected
        if (!this.modificationData.loanNumbers || this.modificationData.loanNumbers.length === 0) {
          notify('At least one Loan Number must be selected', 'error', getNotificationTimeout());
          return;
        }
        try{
          let request: ITaskRequest = new TaskRequest();
          let requestType =this.requestTypes.find((t) => t.name == this.modificationData.requestType)
          request.templateId = requestType.id;
          request.name = requestType.name
          const firstLoanNumber = this.modificationData.loanNumbers.length > 0
            ? (typeof this.modificationData.loanNumbers[0] === 'string'
                ? this.modificationData.loanNumbers[0]
                : this.modificationData.loanNumbers[0].loanNumber)
            : 'Modification Request';
          request.referenceName = firstLoanNumber;
          request.referenceType = requestType.referenceType;
          let formData : FormData = new FormData()
          formData.append('templateId', request.templateId);
          formData.append('name', request.name);
          formData.append('referenceName', request.referenceName);

          // Transform modificationData to send loanNumbers as array of strings
          const dataToSend = this.appendDealNameToSubmission({
            ...this.modificationData,
            loanNumbers: this.modificationData.loanNumbers.map((item: any) =>
              typeof item === 'string' ? item : item.loanNumber
            ),
            newInvestor: this.modificationData.newInvestor || false,
            isUrgent: this.modificationData.isUrgent || false,
          });
          formData.append('requestJsonData', this.removeProxyData(JSON.stringify(dataToSend)));
          formData.append('referenceType', request.referenceType);

          if(this.isEditable)
            formData.append('parentId', this.task.parentId);

          this.files.forEach((f) => {formData.append('files', f, f.name)});
          this.isLoading = true;
          const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
          sendNotificationMessage(`${response[1].data[0].task.taskId} task created`, this.messageService);
          this.isLoading = false;
          this.formElement.nativeElement.reset();
          this.formElement.nativeElement.submit();
        }
        catch(err: any)
        {
          this.isLoading = false;
          this.loggingService.logError(err, SeverityLevel.Error);
          notify(err.message, 'error', getNotificationTimeout());
        }
      }
    }

    onFileUpload(file: File)
    {
      this.files.push(file);
    }

   onFileRemove(file: File)
   {
    if(file != null && file != undefined)
    {
      this.files = this.files.filter(f => f !== file);
    }
   }

    onLoanNumberChanged(e: any): void {
      if (e.value) {

      }
    }

    onGridShowInactiveLoansChange(checked: boolean): void {
      this.showInactiveLoans = checked;
      this.updateCheckboxDisabledState();
    }

    onGridEditModeChange(inEditMode: boolean): void {
      this.isGridInEditMode = inEditMode;
      this.updateCheckboxDisabledState();
    }

    onGridLoanAmountsChange(loanAmounts: any[]): void {
      this.modificationData.loanNumbers = loanAmounts;
      this.updateCheckboxDisabledState();
    }

    override onShowInactiveLoansChanged(e: any): void {
      this.showInactiveLoans = e.value;
      // Call parent implementation
      super.onShowInactiveLoansChanged(e);
    }

    private updateLoanDataInFormConfig(): void {
      if (!this.currentFormInstance) {
        return;
      }
      const loanEditor = this.currentFormInstance.getEditor('loanNumber');
      if (loanEditor) {
        loanEditor.option('items', this.loanNumbers);
      }
    }

    onFormContentReady(e: any): void {
      this.currentFormInstance = e.component;
    }

    onFieldDataChanged(e: any): void {
      // Handle field changes if needed
      if (e.dataField === 'loanNumber' && e.value) {
        this.onLoanNumberChanged(e);
      }
    }

    private normalizeLoanNumbersForGrid(): void {
      if (!this.modificationData) {
        return;
      }

      if (!Array.isArray(this.modificationData.loanNumbers)) {
        this.modificationData.loanNumbers = [];
        return;
      }

      if (
        this.modificationData.loanNumbers.length > 0 &&
        typeof this.modificationData.loanNumbers[0] === 'string'
      ) {
        this.modificationData.loanNumbers = (this.modificationData.loanNumbers as string[]).map((loanNumber) => ({
          key: this.generateUniqueId(),
          loanNumber,
        }));
      }
    }

    override async ngOnInit() {
      try{
        this.isLoading = true;
        let requestType =  await lastValueFrom( this.taskDataService.getTaskTemplatesByType(this.taskTemplateName));
        this.requestTypes = requestType[1];

        await super.ngOnInit();
        this.isEditable = this.task != null ? true : false;

        if(this.isEditable)
        {
          this.modificationData = FormUtils.mapFromTask<TaskModificationData>(this.task, TaskModificationData);
          // console.log(this.modificationData);
        }
        else{
          this.modificationData = new TaskModificationData();
        }

        this.normalizeLoanNumbersForGrid();

        this.isLoading = false;
      }
      catch(err: any)
      {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, 'error', getNotificationTimeout());
      }
    }

    removeProxyData(data: string): string {
      if(data) {
        let json = JSON.parse(data);
        delete json["fileUploadProxy"];
        return JSON.stringify(json);
      }
      return "{}";
    }
  }
